import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import CircularProgress from '@mui/material/CircularProgress';

import { SparkIteration, SparkJob } from '../../types/sparkTypes';
import { useAtlasStore, AtlasState } from '../../../../store/atlasStore';
import { atlasClient } from '../../../../api/atlasClient';
import { ImageLightbox } from '../../../../components/ImageLightbox';
import SparkActionModal from '../modals/SparkActionModal';
import { adaptLibraryItemToIteration } from '../../utils/libraryAdapter';
import styles from '../../styles/ImageSlot.module.css';

const IMAGE_ACTIONS = ['generate_images', 'generate_images_raw', 'edit_image'];

interface ImageSlotProps {
  elementId: string;
  elementLabel: string;
  fileId: string | null;
  index: number;
  cardinality: number;
  isReadOnly: boolean;
  iteration: SparkIteration;
  projectFqn: string;
  capabilities: { canEditMedia: boolean; canUseMediaAI: boolean };
  editorMode: 'iteration' | 'item';
  onIterationUpdated: (updated: SparkIteration) => void;
  sizeClass?: string;
}

const ImageSlot: React.FC<ImageSlotProps> = ({
  elementId,
  elementLabel,
  fileId,
  index,
  cardinality,
  isReadOnly,
  iteration,
  projectFqn,
  capabilities,
  editorMode,
  onIterationUpdated,
  sizeClass: sizeClassOverride,
}) => {
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isGeneratingLocal, setIsGeneratingLocal] = useState(false);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadSparkImageAlternative = useAtlasStore((state: AtlasState) => state.uploadSparkImageAlternative);
  const uploadLibraryItemImageAlternative = useAtlasStore((state: AtlasState) => state.uploadLibraryItemImageAlternative);
  const setSparkUserInteractionBlockingRefresh = useAtlasStore(
    (state: AtlasState) => state.setSparkUserInteractionBlockingRefresh
  );
  const currentSparkJobs = useAtlasStore((state: AtlasState) => state.currentSparkJobs);

  const isItem = editorMode === 'item';

  const isImageJobForSlot = (job: SparkJob) => {
    const args = job._action_args;
    if (!args || !IMAGE_ACTIONS.includes(args.action || '')) return false;
    const inner = args.action_args;
    if (!inner || inner.image_element_name !== elementId) return false;
    // For edit_image jobs, match the specific file being edited to this slot
    if (args.action === 'edit_image' && inner.image_file_id && inner.image_file_id !== fileId) return false;
    if (isItem) {
      return job._owner_fqns?.includes(iteration.fqn) ?? false;
    }
    return inner.input_iteration_fqn === iteration.fqn;
  };

  const hasActiveJob = currentSparkJobs?.some(
    (job: SparkJob) => (job._status === 'pending' || job._status === 'running') && isImageJobForSlot(job)
  ) ?? false;

  // Clear local flag once the job is visible in the store, or if the job finished (failed/completed)
  // before the next poll caught it as active
  const hasTerminalJob = isGeneratingLocal && !hasActiveJob
    ? currentSparkJobs?.some((job: SparkJob) => (job._status === 'failed' || job._status === 'completed') && isImageJobForSlot(job)) ?? false
    : false;

  React.useEffect(() => {
    if (isGeneratingLocal && (hasActiveJob || hasTerminalJob)) {
      setIsGeneratingLocal(false);
    }
  }, [isGeneratingLocal, hasActiveJob, hasTerminalJob]);

  const isGenerating = isGeneratingLocal || hasActiveJob;
  const hasImage = !!fileId;
  const isSingle = cardinality === 1;
  const showOverlay = !isReadOnly && capabilities.canEditMedia;
  const allowAI = capabilities.canUseMediaAI;

  const sizeClass = sizeClassOverride ?? (isSingle ? styles.imageSlotSingle : styles.imageSlotMulti);

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!fileId) return;
    setLightbox({
      src: atlasClient.getAssetUrl(fileId, { full: true }),
      alt: `${elementLabel} slot ${index + 1}`,
    });
  };

  const handleUploadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    if (!file) return;

    if (!(file.type === 'image/png' || file.type === 'image/jpeg')) {
      toast.error('Please select a PNG or JPEG image.');
      return;
    }

    setIsUploading(true);
    setSparkUserInteractionBlockingRefresh(true);

    try {
      if (isItem) {
        const updatedItem = await uploadLibraryItemImageAlternative(iteration.fqn as any, elementId, file);
        onIterationUpdated(adaptLibraryItemToIteration(updatedItem) as SparkIteration);
      } else {
        const updatedIteration = await uploadSparkImageAlternative(iteration.fqn, elementId, file);
        onIterationUpdated(updatedIteration);
      }
      toast.success('Image uploaded successfully.');
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setSparkUserInteractionBlockingRefresh(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleGenerate = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsGenerateModalOpen(true);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!fileId) return;
    setIsEditModalOpen(true);
  };

  return (
    <>
      <div className={`${styles.imageSlot} ${sizeClass}`}>
        {hasImage ? (
          <img
            className={styles.imageSlotImage}
            src={atlasClient.getAssetUrl(fileId)}
            alt={`${elementLabel} slot ${index + 1}`}
          />
        ) : (
          <div className={styles.imageSlotEmpty}>
            <ImageIcon style={{ fontSize: cardinality === 1 ? 48 : 32 }} />
          </div>
        )}

        {showOverlay && (
          <div className={styles.imageSlotOverlay}>
            <div className={styles.imageSlotActions}>
              {hasImage && (
                <button
                  className={styles.imageSlotAction}
                  onClick={handleView}
                  title="View full size"
                  type="button"
                >
                  <ZoomInIcon style={{ fontSize: 18 }} />
                </button>
              )}
              <button
                className={styles.imageSlotAction}
                onClick={handleUploadClick}
                title="Upload image"
                type="button"
              >
                <FileUploadIcon style={{ fontSize: 18 }} />
              </button>
              {allowAI && (
                <button
                  className={`${styles.imageSlotAction} ${styles.imageSlotActionAI}`}
                  onClick={handleGenerate}
                  title="Generate with AI"
                  type="button"
                >
                  <AutoAwesomeIcon style={{ fontSize: 18 }} />
                </button>
              )}
              {hasImage && allowAI && (
                <button
                  className={`${styles.imageSlotAction} ${styles.imageSlotActionAI}`}
                  onClick={handleEdit}
                  title="Edit with AI"
                  type="button"
                >
                  <EditIcon style={{ fontSize: 18 }} />
                </button>
              )}
            </div>
          </div>
        )}

        {isUploading && (
          <div className={styles.imageSlotUploading}>
            <CircularProgress size={28} />
          </div>
        )}

        {!isUploading && isGenerating && (
          <div className={styles.imageSlotGenerating}>
            <CircularProgress size={24} sx={{ color: '#7C3AED' }} />
            <span className={styles.imageSlotGeneratingLabel}>Generating…</span>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
      </div>

      <SparkActionModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        title="Generate Image"
        description="Generate an image based on your guidance."
        actionName="generate_images"
        projectFqn={isItem ? null : projectFqn}
        libraryItemFqn={isItem ? (iteration.fqn as any) : null}
        isGuidanceRequired={true}
        guidancePlaceholder="Describe the image you want — be specific and detailed about subject, style, composition, and mood"
        showAutoEnhanceToggle={true}
        autoEnhanceTooltip="Enrich your prompt with details from the creative content, style guide, and project context"
        onSuccess={() => {
          setIsGeneratingLocal(true);
          onIterationUpdated(iteration);
        }}
        actionArgs={{
          ...(isItem
            ? { image_element_name: elementId }
            : { input_iteration_fqn: iteration.fqn, image_element_name: elementId }
          ),
        }}
      />

      <SparkActionModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Image"
        description="Edit the selected image with AI based on your guidance."
        actionName="edit_image"
        projectFqn={isItem ? null : projectFqn}
        libraryItemFqn={isItem ? (iteration.fqn as any) : null}
        isGuidanceRequired={true}
        guidancePlaceholder="Describe how you want the image altered — be specific about what to change, add, or remove"
        thumbnailUrl={fileId ? atlasClient.getAssetUrl(fileId) : undefined}
        thumbnailAlt={fileId ? `Image to edit: ${fileId}` : undefined}
        onSuccess={() => {
          setIsGeneratingLocal(true);
          onIterationUpdated(iteration);
        }}
        actionArgs={{
          ...(isItem
            ? { image_element_name: elementId, image_file_id: fileId }
            : { input_iteration_fqn: iteration.fqn, image_element_name: elementId, image_file_id: fileId }
          ),
        }}
      />

      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};

export default ImageSlot;
